import { Link } from 'react-router-dom';
import { ArrowRight, Clock, User } from 'lucide-react';
import { getCurrentYear, processDynamicTitle } from '@/lib/dateHelpers';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface FeaturedArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  published_at: string | null;
  category?: { name: string; slug: string } | null;
}

interface FeaturedArticlesProps {
  articles: FeaturedArticle[];
  isLoading?: boolean;
}

export function FeaturedArticles({ articles, isLoading }: FeaturedArticlesProps) {
  if (isLoading) {
    return (
      <section className="py-16">
        <div className="container-wide">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <h2 className="font-display text-3xl font-bold">Artículos Destacados</h2>
              <p className="mt-2 text-muted-foreground">
                Nuestras guías más completas y populares
              </p>
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="overflow-hidden">
                <div className="aspect-[16/10] animate-pulse bg-muted" />
                <CardContent className="p-5">
                  <div className="mb-3 h-4 w-24 animate-pulse rounded bg-muted" />
                  <div className="mb-2 h-6 animate-pulse rounded bg-muted" />
                  <div className="h-12 animate-pulse rounded bg-muted" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Demo articles if none in database
  const currentYear = getCurrentYear();
  const displayArticles: FeaturedArticle[] = articles.length > 0 ? articles : [
    {
      id: '1',
      title: `Las 10 Mejores Sillas Ergonómicas para Oficina en ${currentYear}`,
      slug: 'mejores-sillas-ergonomicas-oficina',
      excerpt: 'Análisis completo de las sillas ergonómicas más recomendadas. Comparamos ajustes, materiales y relación calidad-precio.',
      featured_image: 'https://images.unsplash.com/photo-1580480055273-228ff5388ef8?w=600&h=400&fit=crop',
      category: { name: 'Ergonómicas', slug: 'sillas-ergonomicas' },
      published_at: new Date().toISOString(),
    },
    {
      id: '2',
      title: `Guía Definitiva: Cómo Elegir la Silla Perfecta Según Tu Postura`,
      slug: 'como-elegir-silla-segun-postura',
      excerpt: 'Todo lo que necesitas saber para elegir la silla ideal: altura, peso, horas de uso y problemas posturales.',
      featured_image: 'https://images.unsplash.com/photo-1589364203596-45afcc9e3b8a?w=600&h=400&fit=crop',
      category: { name: 'Postura', slug: 'postura' },
      published_at: new Date().toISOString(),
    },
    {
      id: '3',
      title: `Sillas Gaming vs Ergonómicas: ¿Cuál Es Mejor Para Tu Espalda?`,
      slug: 'sillas-gaming-vs-ergonomicas',
      excerpt: 'Comparamos las sillas gaming con las ergonómicas tradicionales. Descubre cuál se adapta mejor a tus necesidades.',
      featured_image: 'https://images.unsplash.com/photo-1598550476439-6847785fcea6?w=600&h=400&fit=crop',
      category: { name: 'Gaming', slug: 'sillas-gaming' },
      published_at: new Date().toISOString(),
    },
  ];

  return (
    <section className="py-16">
      <div className="container-wide">
        {/* Header */}
        <div className="mb-10 flex items-end justify-between">
          <div>
            <h2 className="font-display text-3xl font-bold">Artículos Destacados</h2>
            <p className="mt-2 text-muted-foreground">
              Nuestras guías más completas y populares
            </p>
          </div>
          <Link to="/guias">
            <Button variant="ghost" className="gap-2">
              Ver todas <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {displayArticles.slice(0, 3).map((article, index) => (
            <Link 
              key={article.id} 
              to={`/articulo/${article.slug}`}
              className="group"
            >
              <Card className="card-hover h-full overflow-hidden border-0 shadow-md">
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={article.featured_image || 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=600&h=400&fit=crop'}
                    alt={article.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    decoding={index === 0 ? 'sync' : 'async'}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  
                  {/* Category Badge */}
                  {article.category && (
                    <Badge className="absolute left-4 top-4 bg-primary/90 hover:bg-primary">
                      {article.category.name}
                    </Badge>
                  )}
                </div>

                {/* Content */}
                <CardContent className="p-5">
                  <h3 className="mb-2 line-clamp-2 font-display text-lg font-semibold transition-colors group-hover:text-primary">
                    {processDynamicTitle(article.title)}
                  </h3>
                  <p className="mb-4 line-clamp-2 text-sm text-muted-foreground">
                    {article.excerpt}
                  </p>
                  
                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      8 min lectura
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Equipo Editorial
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}